package com.sweetlime.bingeamigo.presentation.features.library

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.LibraryTvShow
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.presentation.components.LibraryTab
import com.sweetlime.bingeamigo.repository.LibraryRepository
import com.sweetlime.bingeamigo.repository.ReviewRepository
import com.sweetlime.bingeamigo.state.LibraryUiState
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class TvLibraryViewModel(
    private val repository: LibraryRepository,
    private val reviewRepository: ReviewRepository,
) : ViewModel() {

    private val _selectedTab = MutableStateFlow(LibraryTab.TRACKING)
    val selectedTab: StateFlow<LibraryTab> = _selectedTab.asStateFlow()

    fun selectTab(tab: LibraryTab) { _selectedTab.value = tab }

    val tracking: StateFlow<List<LibraryTvShow>> = repository.getTvShowTracking()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val completed: StateFlow<List<LibraryTvShow>> = repository.getTvShowCompleted()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val favorites: StateFlow<List<LibraryTvShow>> = repository.getTvShowFavorites()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val counts: StateFlow<Map<LibraryTab, Int>> = combine(
        tracking, completed, favorites
    ) { tracking, completed, favorites ->
        mapOf(
            LibraryTab.TRACKING   to tracking.size,
            LibraryTab.COMPLETED  to completed.size,
            LibraryTab.FAVORITES  to favorites.size
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyMap())

    // --- Community ratings (show id → avgRating) ---
    private val _communityRatings = MutableStateFlow<Map<Int, Double>>(emptyMap())
    val communityRatings: StateFlow<Map<Int, Double>> = _communityRatings.asStateFlow()

    private val _uiState = MutableStateFlow<LibraryUiState>(LibraryUiState.Idle)
    val uiState: StateFlow<LibraryUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            combine(tracking, completed, favorites) { tr, co, fav ->
                (tr + co + fav).map { it.tvShow.id }.distinct().sorted()
            }.distinctUntilChanged().collect { ids ->
                if (ids.isNotEmpty()) {
                    val docIds = ids.map { "SHOW_$it" }
                    val summaries = reviewRepository.getContentSummaries(docIds)
                    _communityRatings.value = summaries.mapNotNull { (key, summary) ->
                        if (summary.avgRating > 0.0) key.removePrefix("SHOW_").toIntOrNull()?.let { id -> id to summary.avgRating } else null
                    }.toMap()
                }
            }
        }
    }

    fun addToTracking(libraryTvShow: LibraryTvShow) {
        viewModelScope.launch {
            runCatching { repository.addTvShowToTracking(libraryTvShow) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to add to tracking") }
        }
    }

    fun removeFromTracking(libraryTvShow: LibraryTvShow) {
        viewModelScope.launch {
            runCatching { repository.removeFromTvShowTracking(libraryTvShow.id.toString()) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to remove from tracking") }
        }
    }

    fun removeFromCompleted(libraryTvShow: LibraryTvShow) {
        viewModelScope.launch {
            runCatching { repository.removeFromTvShowCompleted(libraryTvShow.id.toString()) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to update") }
        }
    }

    fun addToFavorites(libraryTvShow: LibraryTvShow) {
        viewModelScope.launch {
            runCatching { repository.addTvShowToFavorites(libraryTvShow) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to add to favorites") }
        }
    }

    fun removeFromFavorites(libraryTvShow: LibraryTvShow) {
        viewModelScope.launch {
            runCatching { repository.removeFromTvShowFavorites(libraryTvShow.id.toString()) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to remove from favorites") }
        }
    }

    fun clearError() {
        _uiState.value = LibraryUiState.Idle
    }

    fun watchedEpisodesForShow(tvShowId: String): Flow<List<LibraryEpisode>> =
        repository.getWatchedEpisodes(tvShowId)

    fun markEpisodeWatched(tvShow: TvShow, episode: LibraryEpisode, totalEpisodesInShow: Int, episodeName: String = "", episodeStillPath: String = "") {
        viewModelScope.launch {
            runCatching { repository.markEpisodeWatched(tvShow, episode, totalEpisodesInShow, episodeName, episodeStillPath) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to mark episode watched") }
        }
    }

    fun markAllSeasonWatched(tvShow: TvShow, seasonEpisodes: List<LibraryEpisode>, lastEpisode: LibraryEpisode, totalEpisodesInShow: Int, lastEpisodeName: String = "", lastEpisodeStillPath: String = "") {
        viewModelScope.launch {
            runCatching { repository.markAllSeasonEpisodesWatched(tvShow, seasonEpisodes, lastEpisode, totalEpisodesInShow, lastEpisodeName, lastEpisodeStillPath) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to mark season watched") }
        }
    }

    fun markEpisodeUnwatched(tvShow: TvShow, episodeId: Int) {
        viewModelScope.launch {
            runCatching { repository.markEpisodeUnwatched(tvShow, episodeId) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to unmark episode") }
        }
    }
}
